import useApi from "@/hooks/useApi";
import AuthService from "@/services/AuthService";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import React, { useState } from "react";
import IconChevronDown from "@/icons/IconChevronDown";
import IconChevronUp from "@/icons/IconChevronUp";

const SUBJECT_OPTIONS = [
  "Billing question",
  "Account access issue",
  "Feature question",
  "Bug or technical issue",
  "Integration help",
  "Other"
];

interface FormState {
  name: string;
  email: string;
  subject: string;
  description: string;
}

export default function ContactSupport() {
  const api = useApi();
  const profile = AuthService.profile;

  const [form, setForm] = useState<FormState>({
    name: `${profile.firstName ?? ""} ${profile.lastName ?? ""}`.trim(),
    email: profile.userName ?? "",
    subject: "",
    description: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  function update(field: keyof FormState, value: string) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.subject || !form.description) {
      setError("Please fill in the subject and description.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.admin.post("Support", form);
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError("Could not send your request. Please try again or email support@velaro.com directly.");
      }
    } catch {
      setError("Could not send your request. Please try again or email support@velaro.com directly.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto mt-10 text-center">
        <div className="text-4xl mb-4">✅</div>
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Request received!</h2>
        <p className="text-slate-600 mb-4">
          Our support team will respond to <strong>{form.email}</strong> as soon as possible,
          typically within one business day.
        </p>
        <SecondaryBlueButton
          label="Submit another request"
          onClick={() => {
            setSubmitted(false);
            setForm((f) => ({ ...f, subject: "", description: "" }));
          }}
        />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto mt-6 px-4">
      {/* How support works */}
      <div className="mb-6 border border-slate-200 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setShowHowItWorks(!showHowItWorks)}
          className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-sm font-medium text-slate-700 text-left transition-colors"
        >
          <span>ℹ️ What happens after I submit?</span>
          {showHowItWorks ? (
            <IconChevronUp width={16} height={16} className="text-slate-500" />
          ) : (
            <IconChevronDown width={16} height={16} className="text-slate-500" />
          )}
        </button>
        {showHowItWorks && (
          <div className="px-4 py-3 text-sm text-slate-600 bg-white border-t border-slate-200 space-y-2">
            <p>
              Your request is emailed directly to our support team at{" "}
              <strong>support@velaro.com</strong>. You'll receive a reply at
              the email address you provide.
            </p>
            <p>
              <strong>Typical response time:</strong> 1 business day for standard requests.
              Urgent issues may respond faster.
            </p>
            <p>
              You can also email <strong>support@velaro.com</strong> directly anytime.
            </p>
          </div>
        )}
      </div>

      {/* Placeholder: live chat button (superadmin-toggled in future) */}
      {/* TODO: When live chat toggle is added to superadmin, show a "Chat with us" button here */}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Your name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
              placeholder="Full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Your email
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
              placeholder="you@example.com"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Subject <span className="text-red-500">*</span>
          </label>
          <select
            value={form.subject}
            onChange={(e) => update("subject", e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover"
            required
          >
            <option value="">Select a topic…</option>
            {SUBJECT_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-hover resize-y"
            rows={6}
            placeholder="Describe your issue or question in as much detail as possible. Include any error messages, steps to reproduce, and what you expected to happen."
            required
          />
        </div>

        {error && (
          <p className="text-red-600 text-sm">{error}</p>
        )}

        <div className="flex items-center gap-3">
          <PrimaryBlueButton
            label={submitting ? "Sending…" : "Send request"}
            onClick={() => {}}
          />
          <span className="text-xs text-slate-400">
            Or email us directly at{" "}
            <a href="mailto:support@velaro.com" className="underline text-slate-500">
              support@velaro.com
            </a>
          </span>
        </div>
      </form>
    </div>
  );
}
